import type { RouteObject } from "react-router-dom";
import { lazy, Suspense, Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { useRoutes } from "react-router-dom";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";

// Lazy-load all secondary pages with retry on ChunkLoadError
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    factory().catch((err: Error) => {
      // Stale chunk after a new deploy: force a hard reload once
      const reloaded = sessionStorage.getItem("chunk-reload");
      if (!reloaded) {
        sessionStorage.setItem("chunk-reload", "1");
        window.location.reload();
        // Return a never-resolving promise so React suspends during reload
        return new Promise(() => {}) as Promise<never>;
      }
      sessionStorage.removeItem("chunk-reload");
      throw err;
    })
  );
}

const About = lazyWithRetry(() => import("@/pages/About"));
const Library = lazyWithRetry(() => import("@/pages/Library"));
const Fiction = lazyWithRetry(() => import("@/pages/Fiction"));
const NonFiction = lazyWithRetry(() => import("@/pages/NonFiction"));
const BookDetail = lazyWithRetry(() => import("@/pages/BookDetail"));
const Blog = lazyWithRetry(() => import("@/pages/Blog"));
const BlogPost = lazyWithRetry(() => import("@/pages/BlogPost"));
const Contact = lazyWithRetry(() => import("@/pages/Contact"));

// Error boundary catches any remaining chunk failures gracefully
interface EBState { hasError: boolean }
class ChunkErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, _info: ErrorInfo) {
    console.error("ChunkErrorBoundary caught:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#FAFAF8] dark:bg-zinc-950 px-6 text-center">
          <p className="font-serif text-xl text-zinc-900 dark:text-white">Something went wrong loading this page.</p>
          <button
            onClick={() => { sessionStorage.removeItem("chunk-reload"); window.location.reload(); }}
            className="px-6 py-3 rounded-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-sm font-semibold hover:bg-[#C5A572] transition-colors"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] dark:bg-zinc-950">
            <div className="w-6 h-6 border-2 border-[#C5A572] border-t-transparent rounded-full animate-spin" />
          </div>
        }
      >
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// Used in @/prerender.tsx
export const routes: RouteObject[] = [
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: "about", element: <SuspenseWrapper><About /></SuspenseWrapper> },
      { path: "library", element: <SuspenseWrapper><Library /></SuspenseWrapper> },
      { path: "fiction", element: <SuspenseWrapper><Fiction /></SuspenseWrapper> },
      { path: "non-fiction", element: <SuspenseWrapper><NonFiction /></SuspenseWrapper> },
      { path: "book/:id", element: <SuspenseWrapper><BookDetail /></SuspenseWrapper> },
      { path: "blog", element: <SuspenseWrapper><Blog /></SuspenseWrapper> },
      { path: "blog/:id", element: <SuspenseWrapper><BlogPost /></SuspenseWrapper> },
      { path: "contact", element: <SuspenseWrapper><Contact /></SuspenseWrapper> },
    ],
  },
];

function App() {
  return useRoutes(routes);
}

export default App;
